import { useState, useEffect, useRef } from 'react'
import { X, ChevronRight, ChevronLeft, Sparkles, CheckCircle } from 'lucide-react'
import { useLanguage } from '../hooks/useLanguage'

export interface TourStep {
  target?: string // CSS selector for highlighting element
  title: string
  content: string
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center'
}

interface TourGuideProps {
  tourId: string // Unique ID for this tour (e.g., 'dashboard', 'transactions')
  steps: TourStep[]
  onComplete?: () => void
}

export default function TourGuide({ tourId, steps, onComplete }: TourGuideProps) {
  const { language } = useLanguage()
  const [currentStep, setCurrentStep] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [highlightStyle, setHighlightStyle] = useState<React.CSSProperties>({})
  const tooltipRef = useRef<HTMLDivElement>(null)

  // Check if tour was already completed
  useEffect(() => {
    const completedTours = JSON.parse(localStorage.getItem('completedTours') || '{}')
    if (!completedTours[tourId]) {
      // Small delay before showing tour
      const timer = setTimeout(() => {
        setIsVisible(true)
        setIsAnimating(true)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [tourId])

  // Update highlight position when step changes
  useEffect(() => {
    if (!isVisible || !steps[currentStep]?.target) {
      setHighlightStyle({})
      return
    }

    const updatePosition = () => {
      const target = document.querySelector(steps[currentStep].target!)
      if (target) {
        // Scroll element into view if not visible
        const rect = target.getBoundingClientRect()
        const isInViewport = (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= window.innerHeight &&
          rect.right <= window.innerWidth
        )
        
        if (!isInViewport) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
          // Wait for scroll to complete before updating position
          setTimeout(() => {
            const newRect = target.getBoundingClientRect()
            const padding = 8
            setHighlightStyle({
              position: 'fixed',
              top: newRect.top - padding,
              left: newRect.left - padding,
              width: newRect.width + padding * 2,
              height: newRect.height + padding * 2,
              borderRadius: '12px',
              transition: 'all 0.3s ease-in-out',
            })
          }, 500)
        } else {
          const padding = 8
          setHighlightStyle({
            position: 'fixed',
            top: rect.top - padding,
            left: rect.left - padding,
            width: rect.width + padding * 2,
            height: rect.height + padding * 2,
            borderRadius: '12px',
            transition: 'all 0.3s ease-in-out',
          })
        }
      }
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition)
    }
  }, [currentStep, isVisible, steps])

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setIsAnimating(false)
      setTimeout(() => {
        setCurrentStep(prev => prev + 1)
        setIsAnimating(true)
      }, 150)
    } else {
      handleComplete()
    }
  }

  const handlePrev = () => {
    if (currentStep > 0) {
      setIsAnimating(false)
      setTimeout(() => {
        setCurrentStep(prev => prev - 1)
        setIsAnimating(true)
      }, 150)
    }
  }

  const handleComplete = () => {
    const completedTours = JSON.parse(localStorage.getItem('completedTours') || '{}')
    completedTours[tourId] = true
    localStorage.setItem('completedTours', JSON.stringify(completedTours))
    setIsVisible(false)
    onComplete?.()
  }

  const handleSkip = () => {
    handleComplete()
  }

  if (!isVisible || steps.length === 0) return null

  const step = steps[currentStep]
  const isLastStep = currentStep === steps.length - 1
  const isFirstStep = currentStep === 0
  const position = step.position || 'center'

  // Calculate tooltip position based on step position preference - always keep in viewport
  const getTooltipPosition = (): React.CSSProperties => {
    const tooltipWidth = Math.min(400, window.innerWidth - 32)
    const tooltipHeight = 220
    const gap = 16
    const margin = 16

    if (position === 'center' || !step.target) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }
    }

    const target = document.querySelector(step.target)
    if (!target) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }
    }

    const rect = target.getBoundingClientRect()
    
    // Helper to clamp values within viewport
    const clampLeft = (left: number) => Math.max(margin, Math.min(left, window.innerWidth - tooltipWidth - margin))
    const clampTop = (top: number) => Math.max(margin, Math.min(top, window.innerHeight - tooltipHeight - margin))

    // Calculate preferred position
    let top: number
    let left: number

    switch (position) {
      case 'bottom':
        top = rect.bottom + gap
        left = rect.left + rect.width / 2 - tooltipWidth / 2
        // If tooltip would go below viewport, place it above
        if (top + tooltipHeight > window.innerHeight - margin) {
          top = rect.top - tooltipHeight - gap
        }
        break
      case 'top':
        top = rect.top - tooltipHeight - gap
        left = rect.left + rect.width / 2 - tooltipWidth / 2
        // If tooltip would go above viewport, place it below
        if (top < margin) {
          top = rect.bottom + gap
        }
        break
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2
        left = rect.left - tooltipWidth - gap
        // If tooltip would go off left edge, place it on right
        if (left < margin) {
          left = rect.right + gap
        }
        break
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2
        left = rect.right + gap
        // If tooltip would go off right edge, place it on left
        if (left + tooltipWidth > window.innerWidth - margin) {
          left = rect.left - tooltipWidth - gap
        }
        break
      default:
        top = window.innerHeight / 2 - tooltipHeight / 2
        left = window.innerWidth / 2 - tooltipWidth / 2
    }

    return {
      position: 'fixed',
      top: clampTop(top),
      left: clampLeft(left),
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm transition-opacity duration-300"
        style={{ opacity: isVisible ? 1 : 0 }}
      />

      {/* Highlight cutout */}
      {step.target && Object.keys(highlightStyle).length > 0 && (
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{
            ...highlightStyle,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6), 0 0 22px rgba(0, 74, 173, 0.45)',
            border: '2px solid rgba(93, 224, 230, 0.85)',
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`fixed z-[10000] w-[90vw] max-w-[400px] bg-white dark:bg-slate-800 rounded-2xl shadow-modal overflow-hidden
          transition-all duration-300 ease-apple
          ${isAnimating ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
        style={getTooltipPosition()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-cyan-500 px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-white" />
              <span className="text-white font-medium text-sm">
                {language === 'it' ? 'Guida' : 'Guide'} {currentStep + 1}/{steps.length}
              </span>
            </div>
            <button
              onClick={handleSkip}
              className="text-white/80 hover:text-white transition-colors"
              title={language === 'it' ? 'Chiudi guida' : 'Close guide'}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {step.title}
          </h3>
          <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
            {step.content}
          </p>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 pb-3">
          {steps.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setIsAnimating(false)
                setTimeout(() => {
                  setCurrentStep(index)
                  setIsAnimating(true)
                }, 150)
              }}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                index === currentStep 
                  ? 'bg-primary-500 w-6' 
                  : index < currentStep 
                    ? 'bg-green-500' 
                    : 'bg-gray-300 dark:bg-slate-600'
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between px-5 pb-5">
          <button
            onClick={handleSkip}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            {language === 'it' ? 'Salta tour' : 'Skip tour'}
          </button>

          <div className="flex gap-2">
            {!isFirstStep && (
              <button
                onClick={handlePrev}
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 
                  bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                {language === 'it' ? 'Indietro' : 'Back'}
              </button>
            )}
            <button
              onClick={handleNext}
              className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white 
                bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              {isLastStep ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  {language === 'it' ? 'Completato!' : 'Done!'}
                </>
              ) : (
                <>
                  {language === 'it' ? 'Avanti' : 'Next'}
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

